import { useEffect, useMemo, useState } from 'react';
import { ErrorBanner, Loading, PageHead } from '../../components/ui';
import {
  controlStatus,
  gapRisk,
  gapsOf,
  guidanceWithoutTokens,
  itemMaturity,
  scoreAudit,
  type ControlStatus,
} from '../../lib/amRag';
import { formatDate } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { AmAuditDetail, AmClientDetail } from '../../lib/types';
import {
  ControlBadge,
  ControlDomains,
  ControlSplit,
  RiskBadge,
  ScoreDial,
  SendPanel,
  SignatureBlock,
} from './amUi';

/**
 * Security risk acceptance form.
 *
 * When a client declines a recommended control, the decision needs to be
 * theirs on paper. Without that, a later incident becomes an argument about
 * whether it was ever raised — this is the document that ends the argument, and
 * it is why the audit bothers rating anything.
 *
 * The form is built from the audit's gaps and partial controls, the operator
 * picks which of them the client is actually being asked to accept, and it
 * prints as a signable document.
 */
export function AmRiskAcceptance({
  auditId,
  organizationName,
  onBack,
}: {
  auditId: string;
  organizationName: string;
  onBack: () => void;
}) {
  const auditResource = useResource<AmAuditDetail>(`/am/audits/${auditId}`);
  const audit = auditResource.data;
  const clientResource = useResource<AmClientDetail>(
    audit ? `/am/clients/${audit.AmClientId}` : null,
  );
  const client = clientResource.data;

  const [selected, setSelected] = useState<Set<string> | null>(null);
  const [recipient, setRecipient] = useState('');

  const gaps = useMemo(() => (audit ? gapsOf(audit.answers) : []), [audit]);

  /* Every gap starts ticked: the default should be the complete list, so
     leaving something off is a deliberate act. */
  useEffect(() => {
    if (gaps.length > 0 && selected === null) {
      setSelected(new Set(gaps.map((g) => g.AmQuestionId)));
    }
  }, [gaps, selected]);

  useEffect(() => {
    if (!client || recipient) return;
    const decisionMaker = client.stakeholders.find((s) => s.Email);
    setRecipient(decisionMaker?.Email ?? client.Email ?? '');
  }, [client, recipient]);

  if (auditResource.loading || clientResource.loading) return <Loading label="Loading the audit" />;
  if (auditResource.error) return <ErrorBanner message={auditResource.error} />;
  if (!audit || !client) return null;

  const chosen = gaps.filter((g) => selected?.has(g.AmQuestionId));
  const { counts, scored, score } = scoreAudit(audit.answers);
  const presented = formatDate(audit.AuditDate);
  const resultLine = `Overall result: ${score}% of assessed controls in place — ${counts['in-place']} in place, ${counts.partial} partially in place, ${counts.gap} ${counts.gap === 1 ? 'gap' : 'gaps'}.`;

  function toggle(questionId: string) {
    setSelected((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(questionId)) next.delete(questionId);
      else next.add(questionId);
      return next;
    });
  }

  const emailBody = [
    `Dear ${client.PrimaryContact ?? client.Name},`,
    '',
    `Following the security review "${audit.Name ?? 'security review'}" presented on ${presented}, ${organizationName} recommends addressing the items below. For any recommendation your organisation elects not to implement at this time, we ask that an authorised representative complete and sign the attached security risk acceptance form.`,
    '',
    resultLine,
    '',
    'RECOMMENDATIONS',
    ...chosen.map((item, n) => {
      const maturity = itemMaturity(item);
      return `${n + 1}. ${item.Text} — Risk level: ${gapRisk(item)}${maturity ? ` — Maturity: ${maturity}` : ''}`;
    }),
    '',
    'Please review the attached form, indicate your decision for each item, and return a signed copy.',
    '',
    'Kind regards,',
    audit.Auditor ?? organizationName,
  ].join('\n');

  const copyText = () => {
    const lines = [
      'SECURITY RISK ACCEPTANCE FORM',
      `${organizationName} · ${audit.Name ?? 'security review'}`,
      '',
      `Client: ${client.Name}`,
      `Date presented: ${presented}`,
      '',
      'SECURITY REVIEW RESULTS',
      resultLine,
      '',
      'RECOMMENDATIONS',
    ];
    chosen.forEach((item, n) => {
      const maturity = itemMaturity(item);
      lines.push(
        `${n + 1}. ${item.Text} [${item.Category ?? 'Other'}] — Risk level: ${gapRisk(item)}${maturity ? ` — Maturity: ${maturity}` : ''}`,
      );
      lines.push(
        '   Decision:  [ ] Decline   [ ] Defer until: ________   [ ] Alternate control: ________',
      );
    });
    lines.push(
      '',
      'RISK SUMMARY',
      `${organizationName} has recommended implementation of the security controls listed above. Not implementing these recommendations may increase the risk of unauthorised access, ransomware, business email compromise, data loss, financial loss, compliance violations and service disruption.`,
      '',
      'CLIENT ACKNOWLEDGEMENT',
      'We acknowledge that the recommendations, the associated risks and the potential impacts have been explained to us. We understand that declining these recommendations may increase our exposure to cybersecurity threats and the resulting damage.',
      '',
      'RISK ACCEPTANCE',
      `We knowingly and voluntarily accept the risks associated with declining or deferring the recommendations indicated above. By declining them we acknowledge that ${organizationName} cannot guarantee the prevention of security incidents arising from the risks described, and that a future incident related to those risks may have been mitigated or prevented had the recommendations been implemented.`,
      '',
      'AUTHORISED REPRESENTATIVE',
      'Name: ______________________   Title: ______________________',
      `Company: ${client.Name}`,
      'Signature: ___________________   Date: _______________________',
    );
    return lines.join('\n');
  };

  return (
    <>
      <div className="no-print">
        <button type="button" className="btn btn-ghost btn-sm mb-4" onClick={onBack}>
          ← Back to the audit
        </button>
        <PageHead
          title="Security risk acceptance form"
          lede="Have the client formally accept the risk of the recommendations they decline or defer. Signed by someone with the authority to accept it."
        />

        <section className="card mb-4">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">Step one</span>
              <h2>Choose the items</h2>
              <p className="sub">
                {chosen.length} of {gaps.length} findings on the form
              </p>
            </div>
          </div>
          <div className="card-body stack" style={{ gap: 'var(--space-2)' }}>
            {gaps.length === 0 ? (
              <p className="small muted">
                This audit has no gap or partial findings, so there is nothing to accept. That is the
                good outcome.
              </p>
            ) : (
              gaps.map((item) => (
                <label key={item.AmQuestionId} className="checkbox">
                  <input
                    type="checkbox"
                    checked={selected?.has(item.AmQuestionId) ?? false}
                    onChange={() => toggle(item.AmQuestionId)}
                  />
                  <ControlBadge response={item.Response} />
                  <span className="grow small">{item.Text}</span>
                  <span className="xs muted">{item.Category}</span>
                  <RiskBadge risk={gapRisk(item)} />
                  {itemMaturity(item) && (
                    <span className="badge badge-neutral">{itemMaturity(item)}</span>
                  )}
                </label>
              ))
            )}
          </div>
        </section>

        <SendPanel
          recipient={recipient}
          onRecipient={setRecipient}
          suggestions={[
            ...client.stakeholders
              .filter((s) => s.Email)
              .map((s) => ({ value: s.Email as string, label: `${s.Name} (${s.Role ?? 'stakeholder'})` })),
            ...(client.Email
              ? [{ value: client.Email, label: client.PrimaryContact ?? client.Name }]
              : []),
          ]}
          subject={`Security risk acceptance form — ${client.Name} — action required`}
          body={emailBody}
          disabled={chosen.length === 0}
          copyLabel="Copy form text"
          copyText={copyText}
        />
      </div>

      {/* ---------- the printable document ---------- */}
      <article className="am-doc">
        <header className="am-doc-head">
          <div>
            <div className="am-doc-brand">{organizationName}</div>
            <h2>Security risk acceptance form</h2>
          </div>
          <div className="am-doc-meta">
            <div>
              <strong>Client:</strong> {client.Name}
            </div>
            <div>
              <strong>Review:</strong> {audit.Name ?? '—'}
            </div>
            <div>
              <strong>Date presented:</strong> {presented}
            </div>
          </div>
        </header>

        {scored > 0 && (
          <>
            <div className="am-doc-title">Security review results</div>
            <div className="am-score-row">
              <ScoreDial score={score} label="In place" />
              <div className="grow">
                <ControlSplit counts={counts as Record<ControlStatus, number>} />
              </div>
            </div>
            <ControlDomains items={audit.answers} />
          </>
        )}

        <div className="am-doc-title">Recommendations</div>
        {chosen.length === 0 ? (
          <p className="muted small">No findings selected.</p>
        ) : (
          <table className="am-doc-table">
            <thead>
              <tr>
                <th style={{ width: 28 }}>#</th>
                <th>Recommended control</th>
                <th style={{ width: 130 }}>Domain</th>
                <th style={{ width: 100 }}>Maturity</th>
                <th style={{ width: 90 }}>Risk</th>
                <th style={{ width: '28%' }}>Client decision</th>
              </tr>
            </thead>
            <tbody>
              {chosen.map((item, n) => (
                <tr key={item.AmQuestionId}>
                  <td>{n + 1}</td>
                  <td>
                    <div className="strong">{item.Text}</div>
                    {guidanceWithoutTokens(item.Guidance) && (
                      <div className="xs muted">{guidanceWithoutTokens(item.Guidance)}</div>
                    )}
                    <div className="xs muted">
                      Current status: {controlStatus(item.Response) === 'gap' ? 'not in place' : 'partially in place'}
                    </div>
                  </td>
                  <td>{item.Category ?? '—'}</td>
                  <td>{itemMaturity(item) || '—'}</td>
                  <td>
                    <RiskBadge risk={gapRisk(item)} />
                  </td>
                  <td className="am-decision">
                    <div>☐ Decline</div>
                    <div>☐ Defer until: __________</div>
                    <div>☐ Alternate control: __________</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="am-doc-title">Risk summary</div>
        <p>
          {organizationName} has recommended implementation of the security controls listed above.
          Not implementing these recommendations may increase the risk of unauthorised access,
          ransomware, business email compromise, data loss, financial loss, compliance violations and
          service disruption.
        </p>

        <div className="am-doc-title">Client acknowledgement</div>
        <p>
          We acknowledge that the recommendations, the associated risks and the potential impacts
          have been explained to us. We understand that declining these recommendations may increase
          our organisation&rsquo;s exposure to cybersecurity threats and the resulting damage. Our
          decision for each recommendation is indicated above.
        </p>

        <div className="am-doc-title">Risk acceptance</div>
        <p>
          We knowingly and voluntarily accept the risks associated with declining or deferring the
          recommendations indicated above. By declining them we acknowledge that {organizationName}{' '}
          cannot guarantee the prevention of security incidents arising from the risks described, and
          that a future incident related to those risks may have been mitigated or prevented had the
          recommendations been implemented.
        </p>

        <SignatureBlock companyName={client.Name} />
      </article>
    </>
  );
}
